import {
  useCreateOperation,
  useFetchSuggestOperations,
} from "@/lib/queries/hooks";
import Dropdown from "@/lib/Dropdown";
import Menu from "@/lib/Menu";
import PlusIcon from "~icons/local/plus";
import { useState } from "react";
import useMeasure from "react-use-measure";
import OperationTypeIcon from "./OperationTypeIcon";
import classNames from "classnames";
import { useParams } from "react-router";
import { components } from "@/lib/ApiSchema";
import { getFullOperationsWidth } from "@/lib/utils";

export default function AddOperation({
  ordinal,
  quantities,
  first,
  generalCalculatorType,
}: {
  ordinal: string;
  quantities: number[];
  generalCalculatorType: components["schemas"]["CalculatorGeneralTypeEnum"];
  first?: boolean;
}) {
  const { data: suggestOperations = [] } = useFetchSuggestOperations();
  const { mutate: createOperation } = useCreateOperation();
  const { teamSlug, partId } = useParams();

  const [searchTerm, setSearchTerm] = useState("");

  const filteredOperations = suggestOperations
    .filter((op) => op.general_type === generalCalculatorType)
    .filter((op) => op.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const additionalPadding = 25; // so that + icon doesn't overflow ActionsColumn
  const width = getFullOperationsWidth(quantities.length) + additionalPadding;

  const [triggerRef, bounds] = useMeasure();

  const trigger = first
    ? ({ expanded }: { expanded: boolean }) => (
        <div
          className={classNames(
            "rounded-full p-1 ring ring-slate-300 hover:bg-slate-300",
            { "bg-slate-300": expanded },
            { "bg-white": !expanded },
          )}
        >
          <PlusIcon className="size-4 text-slate-600 fill-current" />
        </div>
      )
    : ({ expanded }: { expanded: boolean }) => (
        <div ref={triggerRef}>
          <div
            className={classNames(
              "flex justify-end group/add-operation hover:bg-blue-300 h-2 relative",
              { "bg-blue-300": expanded },
            )}
            style={{ width: bounds.width > width ? width : "inherit" }}
          >
            <div
              className={classNames(
                "bg-blue-400 hover:bg-blue-200 rounded-full p-1 group-hover/add-operation:visible absolute shadow-lg z-10 mt-[-0.65rem]",
                { invisible: !expanded },
              )}
            >
              <PlusIcon className="size-5 text-white hover:text-blue-600 fill-current" />
            </div>
          </div>
        </div>
      );

  return (
    <Dropdown trigger={trigger} placement="bottom" strategy="fixed">
      <div className="min-w-64">
        <div className="px-4 py-1 text-sm font-semibold text-slate-600">
          Select operation to add…
        </div>
        <input
          placeholder="Filter operations..."
          className="w-full px-4 py-1 border rounded text-sm"
          autoFocus
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Menu
          className="text-gray-800 max-h-96 overflow-y-auto"
          itemGroups={[
            filteredOperations.map(({ name, type }) => ({
              label: (
                <div className="flex items-center gap-1">
                  <OperationTypeIcon operationType={type} className="size-4" />
                  <span className="ml-2">{name}</span>
                </div>
              ),
              click: () => {
                createOperation({
                  params: { path: { team_slug: teamSlug! } },
                  body: {
                    name,
                    type,
                    ordinal,
                    draft: {},
                    requested_part_rev_id: partId!,
                  },
                });
              },
            })),
          ]}
        />
      </div>
    </Dropdown>
  );
}
